import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import '../styles/pages.css';

const IP = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const Icon = {
  Plus:    (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M5 12h14M12 5v14" /></svg>),
  Compass: (p) => (<svg width="18" height="18" {...IP} {...p}><circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" /></svg>),
  CompassBig: (p) => (<svg width="40" height="40" {...IP} strokeWidth="1.5" {...p}><circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" /></svg>),
  Pencil:  (p) => (<svg width="12" height="12" {...IP} {...p}><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /></svg>),
  Trash:   (p) => (<svg width="12" height="12" {...IP} {...p}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>),
  Copy:    (p) => (<svg width="12" height="12" {...IP} {...p}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>),
};

const EMPTY_FORM = { name: '', source: '', medium: '', campaign: '', term: '', content: '' };
const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'];

export default function UTMTemplatesPage() {
  const { workspace } = useAuth();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!workspace?.workspace_id) return;
    fetchPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.workspace_id]);

  const fetchPresets = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/workspaces/${workspace.workspace_id}/utm-presets`);
      setPresets(res.data || []);
    } catch {
      toast.error('Failed to load UTM templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      if (editing) {
        await api.put(`/workspaces/${workspace.workspace_id}/utm-presets/${editing}`, form);
        toast.success('Template updated');
        setEditing(null);
      } else {
        await api.post(`/workspaces/${workspace.workspace_id}/utm-presets`, form);
        toast.success('Template created');
        setAdding(false);
      }
      setForm(EMPTY_FORM);
      fetchPresets();
    } catch (err) {
      toast.error(err.message || 'Failed to save template');
    }
  };

  const handleDelete = async (presetId) => {
    try {
      await api.delete(`/workspaces/${workspace.workspace_id}/utm-presets/${presetId}`);
      setPresets((p) => p.filter((x) => x.preset_id !== presetId));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const copyUtm = (preset) => {
    const params = UTM_KEYS
      .filter((k) => preset[k])
      .map((k) => `utm_${k}=${encodeURIComponent(preset[k])}`)
      .join('&');
    navigator.clipboard.writeText(params);
    toast.success('UTM params copied');
  };

  const startEdit = (preset) => {
    setEditing(preset.preset_id);
    setForm({
      name: preset.name,
      source: preset.source || '',
      medium: preset.medium || '',
      campaign: preset.campaign || '',
      term: preset.term || '',
      content: preset.content || '',
    });
    setAdding(true);
  };

  return (
    <div className="lfp-root" data-testid="utm-templates-page">
      <div className="lfp-header">
        <div>
          <div className="lfp-label">Insights · {presets.length} templates</div>
          <h1 className="lfp-title">UTM <em>templates</em>.</h1>
          <p className="lfp-sub">
            Reusable tracking parameter presets that auto-apply when you tag a link.
          </p>
        </div>
        <button
          type="button"
          className="lfp-btn lfp-btn-coral"
          onClick={() => {
            setAdding(true);
            setEditing(null);
            setForm(EMPTY_FORM);
          }}
        >
          <Icon.Plus /> New template
        </button>
      </div>

      {adding && (
        <div className="lfp-panel" style={{ marginBottom: 16 }}>
          <div className="lfp-panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {editing ? 'Edit template' : 'New UTM template'}
            </div>
            <input
              type="text"
              className="lfp-input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              placeholder="e.g. Q2 Paid Social"
              autoFocus
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
              {UTM_KEYS.map((k) => (
                <div key={k} className="lfp-field">
                  <label>utm_{k}</label>
                  <input
                    type="text"
                    value={form[k]}
                    onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                    placeholder={k}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="lfp-btn lfp-btn-coral"
                onClick={handleSave}
                disabled={!form.name.trim()}
              >
                {editing ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                className="lfp-btn lfp-btn-outline"
                onClick={() => { setAdding(false); setEditing(null); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="lfp-panel">
          <div className="lfp-empty">
            <div className="lfp-skeleton" style={{ height: 14, width: 160, margin: '0 auto 12px' }} />
            <div className="lfp-skeleton" style={{ height: 10, width: 240, margin: '0 auto' }} />
          </div>
        </div>
      ) : presets.length === 0 ? (
        <div className="lfp-panel">
          <div className="lfp-empty">
            <Icon.CompassBig style={{ opacity: 0.3, margin: '0 auto 8px', display: 'block' }} />
            <div className="lfp-empty-title">No UTM templates yet</div>
            <p className="lfp-empty-sub">
              Create reusable UTM presets to speed up link creation and keep tracking consistent.
            </p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {presets.map((preset) => (
            <div key={preset.preset_id} className="lfp-panel">
              <div
                className="lfp-panel-body"
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 7,
                      background: 'var(--coral-soft)',
                      color: 'var(--coral-deep)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon.Compass />
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{preset.name}</div>
                  <div style={{ display: 'inline-flex', gap: 4 }}>
                    <button
                      type="button"
                      className="lfp-icon-btn"
                      onClick={() => copyUtm(preset)}
                      title="Copy UTM params"
                    >
                      <Icon.Copy />
                    </button>
                    <button
                      type="button"
                      className="lfp-icon-btn"
                      onClick={() => startEdit(preset)}
                      title="Edit template"
                    >
                      <Icon.Pencil />
                    </button>
                    <button
                      type="button"
                      className="lfp-icon-btn lfp-danger"
                      onClick={() => {
                        if (window.confirm(`Delete template "${preset.name}"?`)) handleDelete(preset.preset_id);
                      }}
                      title="Delete template"
                    >
                      <Icon.Trash />
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {UTM_KEYS.filter((k) => preset[k]).length === 0 ? (
                    <span className="lfp-row-muted">No UTM params set.</span>
                  ) : (
                    UTM_KEYS.filter((k) => preset[k]).map((k) => (
                      <span
                        key={k}
                        style={{
                          fontFamily: "'Geist Mono', ui-monospace, monospace",
                          fontSize: 11,
                          padding: '3px 8px',
                          background: 'var(--bg-warm)',
                          color: 'var(--ink-2)',
                          borderRadius: 6,
                          border: '1px solid var(--line)',
                        }}
                      >
                        <span style={{ color: 'var(--ink-faint)' }}>utm_{k}=</span>
                        <span style={{ color: 'var(--ink)' }}>{preset[k]}</span>
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
